import { Menu, MenuProps, Popover, Switch } from "antd";
import React, { FC, useMemo, useState } from "react";
import "./Actions.scss";
import { useTranslation } from "react-i18next";
import useActions from "../../../hooks/useActions";
import { DrawerType } from "../../../interfaces/drawer";
import ReprocessConfirmModal from "./ReprocessConfirmModal";
import ConfirmationModal from "../../../../common/modals/confirmationModal/ConfirmationModal";

type Props = {
  disabled: boolean;
};

const Actions: FC<Props> = ({ disabled }) => {
  const { t } = useTranslation("annotationSideControls");

  const {
    toggleHideNonRequiredEntitiesForUser,
    downloadPdfAndClose,
    removeAllAnnotationsAndClose,
    handleRenormalizeAllAnnotations,
    hideNonRequiredEntities,
    open,
    setOpen,
    openDrawerAndSetType,
    handleReExport,
    handleReProcess,
    showReExport,
    showReProcess,
    isAssignedToCurrentUser,
    isProcessing,
    processingAction,
    documentTypes,
  } = useActions();

  const [reprocessModalOpen, setReprocessModalOpen] = useState(false);
  const [reExportModalOpen, setReExportModalOpen] = useState(false);

  const confirmReExport = async () => {
    await handleReExport();
    setReExportModalOpen(false);
  };

  const items: MenuProps["items"] = useMemo(
    () => [
      {
        icon: <i className="bi bi-eraser" />,
        disabled: disabled,
        label: (
          <span className="item-label">
            {t("annotationSideControlsHeader.general")}
          </span>
        ),
        type: "group",
        key: "clearAllAnnotations",
        onClick: removeAllAnnotationsAndClose,
        children: [
          {
            icon: <i className="bi bi-info-circle" />,
            label: (
              <span className="item-label">
                {t("annotationSideControlsHeader.moreDetails")}
              </span>
            ),
            key: "moreDetails",
            onClick: () => openDrawerAndSetType(DrawerType.DETAILS),
          },
          {
            icon: <i className="bi bi-clock-history" />,
            label: (
              <span className="item-label">
                {t("annotationSideControlsHeader.annotationEditHistory")}
              </span>
            ),
            key: "annotationHistory",
            onClick: () => openDrawerAndSetType(DrawerType.ANNOTATION_HISTORY),
          },
          {
            icon: <i className="bi bi-download" />,
            label: "Download",
            key: "downloadFile",
            onClick: downloadPdfAndClose,
          },
        ],
      },
      {
        icon: <i className="bi bi-eraser" />,
        disabled: disabled,
        label: (
          <span className="item-label">
            {t("annotationSideControlsHeader.annotations")}
          </span>
        ),
        type: "group",
        key: "annotations",
        onClick: removeAllAnnotationsAndClose,
        children: [
          {
            icon: <i className="bi bi-eraser" />,
            disabled: disabled,
            label: (
              <span className="item-label">
                {t("annotationSideControlsHeader.clearAllAnnotations")}
              </span>
            ),
            key: "clearAllAnnotations",
            onClick: removeAllAnnotationsAndClose,
          },
          {
            icon: <i className="bi bi-arrow-repeat" />,
            disabled: disabled,
            label: (
              <span className="item-label">
                {t("annotationSideControlsHeader.renormalizeAllAnnotations")}
              </span>
            ),
            key: "renormalizeAllAnnotations",
            onClick: handleRenormalizeAllAnnotations,
          },
          {
            icon: <i className="bi bi-eye-slash" />,
            disabled: disabled,
            label: (
              <>
                <span className="switch-label">
                  {t("groupBlocksHeader.entities")}
                </span>
                <Switch disabled={disabled} checked={hideNonRequiredEntities} />
              </>
            ),
            key: "hideNonRequiredEntities",
            onClick: toggleHideNonRequiredEntitiesForUser,
          },
        ],
      },
      ...(showReExport || showReProcess
        ? [
            {
              icon: <i className="bi bi-gear" />,
              label: (
                <span className="item-label">
                  {t("annotationSideControlsHeader.administration")}
                </span>
              ),
              type: "group" as const,
              key: "administration",
              children: [
                ...(showReExport
                  ? [
                      {
                        icon:
                          isProcessing && processingAction === "reexport" ? (
                            <i className="bi bi-box-arrow-up spinning" />
                          ) : (
                            <i className="bi bi-box-arrow-up" />
                          ),
                        label:
                          isProcessing && processingAction === "reexport"
                            ? t(
                                "annotationSideControlsHeader.reExporting",
                                "Exporting...",
                              )
                            : t("annotationSideControlsHeader.reExport"),
                        key: "reexport",
                        danger: true,
                        disabled: isProcessing,
                        onClick: () => {
                          setOpen(false);
                          setReExportModalOpen(true);
                        },
                      },
                    ]
                  : []),
                ...(showReProcess
                  ? [
                      {
                        icon:
                          isProcessing && processingAction === "reprocess" ? (
                            <i className="bi bi-arrow-repeat spinning" />
                          ) : (
                            <i className="bi bi-arrow-repeat" />
                          ),
                        label:
                          isProcessing && processingAction === "reprocess"
                            ? t(
                                "annotationSideControlsHeader.reProcessing",
                                "Processing...",
                              )
                            : t("annotationSideControlsHeader.reProcess"),
                        key: "reprocess",
                        danger: true,
                        disabled: isProcessing || !isAssignedToCurrentUser,
                        onClick: () => {
                          setOpen(false);
                          setReprocessModalOpen(true);
                        },
                      },
                    ]
                  : []),
              ],
            },
          ]
        : []),
    ],
    [
      disabled,
      hideNonRequiredEntities,
      removeAllAnnotationsAndClose,
      handleRenormalizeAllAnnotations,
      downloadPdfAndClose,
      openDrawerAndSetType,
      t,
      toggleHideNonRequiredEntitiesForUser,
      showReExport,
      showReProcess,
      isAssignedToCurrentUser,
      setOpen,
      isProcessing,
      processingAction,
    ],
  );

  return (
    <>
      <Popover
        className="actions"
        open={open}
        placement="rightTop"
        content={
          <Menu selectable={false} className="popover-menu" items={items} />
        }
        trigger="click"
        onOpenChange={() => setOpen(!open)}
      >
        <i className="bi bi-three-dots-vertical actions" />
      </Popover>
      <ReprocessConfirmModal
        open={reprocessModalOpen}
        documentTypes={documentTypes}
        confirmLoading={isProcessing && processingAction === "reprocess"}
        onClose={() => setReprocessModalOpen(false)}
        onConfirm={(documentTypeId) => handleReProcess(documentTypeId)}
      />
      <ConfirmationModal
        open={reExportModalOpen}
        type="danger"
        title={t("reExportModal.title")}
        description={t("reExportModal.description")}
        confirmText={t("reExportModal.confirm")}
        cancelText={t("reExportModal.cancel")}
        confirmLoading={isProcessing && processingAction === "reexport"}
        onCancel={() => setReExportModalOpen(false)}
        onClose={() => setReExportModalOpen(false)}
        onConfirm={confirmReExport}
      />
    </>
  );
};

export default Actions;
